import React, { useState } from 'react';
import { Modal, Typography, Space, App } from 'antd';
import { CheckCircleOutlined, CancelOutlined, DescriptionOutlined } from '@mui/icons-material';
import { PrimaryButton, OutlineButton } from '../../Components/Buttons/Buttons';
import { getTheme } from '../../../theme/theme';
import QuoteAcceptModal from './QuoteAcceptModal';
import QuoteRejectModal from './QuoteRejectModal';

const { Title, Text, Paragraph } = Typography;

/**
 * Modal for client to accept or reject a quote
 * @param {Object} props - Component properties
 * @param {boolean} props.visible - Whether the modal is visible
 * @param {Function} props.onCancel - Function called when modal is cancelled
 * @param {Object} props.order - Order object containing quote information
 * @param {Function} props.onSuccess - Function called when quote response is successful
 */
const QuoteResponseModal = ({
    visible,
    onCancel,
    order,
    onSuccess
}) => {
    const { message } = App.useApp();
    const theme = getTheme();

    const [acceptModalVisible, setAcceptModalVisible] = useState(false);
    const [rejectModalVisible, setRejectModalVisible] = useState(false);

    // Handle opening quote document
    const handleViewQuote = () => {
        if (order?.quoteUrl) {
            window.open(order.quoteUrl, '_blank', 'noopener,noreferrer');
        } else {
            message.error('Quote document not available');
        }
    };

    // Handle accept button click
    const handleAccept = () => {
        setAcceptModalVisible(true);
    };

    // Handle reject button click
    const handleReject = () => {
        setRejectModalVisible(true);
    };

    // Handle accept confirmation
    const handleAcceptConfirm = () => {
        setAcceptModalVisible(false);
        onSuccess('accepted');
        onCancel();
    };

    // Handle reject confirmation
    const handleRejectConfirm = (reason) => {
        setRejectModalVisible(false);
        onSuccess('rejected', reason);
        onCancel();
    };

    // Handle cancel accept
    const handleAcceptCancel = () => {
        setAcceptModalVisible(false);
    };

    // Handle cancel reject
    const handleRejectCancel = () => {
        setRejectModalVisible(false);
    };

    return (
        <>
            <Modal
                title={
                    <Space align="center">
                        <DescriptionOutlined style={{ color: theme.token.colorPrimary }} />
                        <Title level={4} style={{ margin: 0, color: theme.token.colorText }}>
                            Quote Response Required
                        </Title>
                    </Space>
                }
                open={visible}
                onCancel={onCancel}
                footer={null}
                width={600}
                centered
                style={{
                    borderRadius: '12px',
                }}
                styles={{
                    body: {
                        padding: '24px',
                        backgroundColor: theme.token.colorBgContainer,
                    }
                }}
            >
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                        <Title level={5} style={{ color: theme.token.colorText, marginBottom: '12px' }}>
                            Quote Details
                        </Title>
                        <Paragraph style={{ color: theme.token.colorTextSecondary }}>
                            A quote has been prepared for your order. Please review the quote document
                            and choose whether to accept or reject the proposal.
                        </Paragraph>
                    </div>

                    {/* Quote Document Section */}
                    <div
                        style={{
                            padding: '16px',
                            backgroundColor: theme.token.colorFillSecondary,
                            borderRadius: '8px',
                            border: `1px solid ${theme.token.colorBorderSecondary}`,
                        }}
                    >
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong style={{ color: theme.token.colorText }}>
                                Quote Document
                            </Text>
                            <Text style={{ color: theme.token.colorTextSecondary }}>
                                Click the button below to view the detailed quote document.
                            </Text>
                            <OutlineButton
                                icon={<DescriptionOutlined />}
                                onClick={handleViewQuote}
                                disabled={!order?.quoteUrl}
                                style={{ marginTop: '8px' }}
                            >
                                View Quote Document
                            </OutlineButton>
                        </Space>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ marginTop: '24px' }}>
                        <Title level={5} style={{ color: theme.token.colorText, marginBottom: '16px' }}>
                            Your Response
                        </Title>
                        <Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
                            <PrimaryButton
                                icon={<CheckCircleOutlined />}
                                onClick={handleAccept}
                                style={{ minWidth: '140px' }}
                            >
                                Accept Quote
                            </PrimaryButton>
                            <OutlineButton
                                icon={<CancelOutlined />}
                                onClick={handleReject}
                                style={{ minWidth: '140px' }}
                            >
                                Reject Quote
                            </OutlineButton>
                        </Space>
                    </div>

                    {/* Help Text */}
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                        <Text
                            style={{
                                color: theme.token.colorTextSecondary,
                                fontSize: '12px',
                                fontStyle: 'italic'
                            }}
                        >
                            Please ensure you have reviewed the quote document before making your decision.
                        </Text>
                    </div>
                </Space>
            </Modal>

            {/* Accept Confirmation Modal */}
            <QuoteAcceptModal
                visible={acceptModalVisible}
                onConfirm={handleAcceptConfirm}
                onCancel={handleAcceptCancel}
                order={order}
            />

            {/* Reject Confirmation Modal */}
            <QuoteRejectModal
                visible={rejectModalVisible}
                onConfirm={handleRejectConfirm}
                onCancel={handleRejectCancel}
                order={order}
            />
        </>
    );
};

export default QuoteResponseModal; 